import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [packageInfo, setPackageInfo] = useState(null);
  const sessionId = searchParams.get('session_id');
  
  // Track page view on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: 'Payment Success',
        page_location: window.location.href,
        page_path: '/success'
      });
    }
  }, []);
  
  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }
    
    pollPaymentStatus();
  }, [sessionId]);
  
  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/payments/status/${sessionId}`);
      const data = response.data;
      
      if (data.payment_status === 'paid') {
        // Enable premium
        localStorage.setItem('zenermind_is_premium', 'true');
        localStorage.setItem('zenermind_package_id', data.package_id);
        
        setPackageInfo(data);
        setStatus('success');
        
        // Track successful payment in Google Analytics
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'purchase', {
            transaction_id: sessionId,
            value: data.amount || 0,
            currency: 'TRY',
            items: [{
              item_id: data.package_id,
              item_name: data.package_name
            }]
          });
        }
        
        // Redirect to app after 3 seconds
        setTimeout(() => {
          navigate('/');
        }, 3000);
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
    } catch (error) {
      console.error('Payment status check error:', error);
      setStatus('error');
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <motion.div
        className="glass-panel max-w-md w-full p-8 space-y-6 text-center"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-violet-400 mx-auto animate-spin" />
            <h2 className="text-2xl font-heading text-slate-100">
              Ödeme Kontrol Ediliyor...
            </h2>
            <p className="text-slate-400 text-sm">
              Lütfen bekleyin, ödemenizi doğruluyoruz.
            </p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto" />
            <h2 className="text-2xl font-heading text-slate-100">
              Ödeme Başarılı!
            </h2>
            <p className="text-slate-400 text-sm">
              {packageInfo?.package_name} paketiniz aktif edildi.
            </p>
            <p className="text-slate-500 text-xs">
              Anasayfaya yönlendiriliyorsunuz...
            </p>
          </>
        )}
        
        {status === 'timeout' && (
          <>
            <h2 className="text-2xl font-heading text-slate-100">
              Zaman Aşımı
            </h2>
            <p className="text-slate-400 text-sm">
              Ödeme durumu kontrol edilemedi. Lütfen e-postanızı kontrol edin.
            </p>
            <button
              onClick={() => navigate('/')}
              className="rounded-full bg-violet-500/20 px-6 py-3 text-sm font-medium text-violet-300 transition-all hover:bg-violet-500/30 border border-violet-500/30"
            >
              Anasayfaya Dön
            </button>
          </>
        )}
        
        {status === 'expired' && (
          <>
            <h2 className="text-2xl font-heading text-slate-100">
              Oturum Süresi Doldu
            </h2>
            <p className="text-slate-400 text-sm">
              Ödeme oturumunun süresi doldu. Lütfen tekrar deneyin.
            </p>
            <button
              onClick={() => navigate('/')}
              className="rounded-full bg-violet-500/20 px-6 py-3 text-sm font-medium text-violet-300 transition-all hover:bg-violet-500/30 border border-violet-500/30"
            >
              Anasayfaya Dön
            </button>
          </>
        )}
        
        {status === 'error' && (
          <>
            <h2 className="text-2xl font-heading text-slate-100">
              Hata
            </h2>
            <p className="text-slate-400 text-sm">
              Ödeme durumu kontrol edilirken bir hata oluştu.
            </p>
            <button
              onClick={() => navigate('/')}
              className="rounded-full bg-violet-500/20 px-6 py-3 text-sm font-medium text-violet-300 transition-all hover:bg-violet-500/30 border border-violet-500/30"
            >
              Anasayfaya Dön
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
};
